import { type FC, useEffect, useState, useRef } from "react";
import Icon from "@ch/components/Icons/Icon";
import KemImage from "@ch/assets/images/kem-speech.svg";
import { toHttpUrl } from "../utils/audioUrl";
import ConcentricCircles from "./ConcentricCircles";

interface KemSpeechPlayingProps {
  audioUrl?: string;
  onStartRecording: () => void;
}

const KemSpeechPlaying: FC<KemSpeechPlayingProps> = ({
  audioUrl,
  onStartRecording,
}) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!audioUrl) return undefined;

    // Create and play audio
    const audio = new Audio(toHttpUrl(audioUrl));
    audioRef.current = audio;

    audio.addEventListener("play", () => setIsSpeaking(true));
    audio.addEventListener("ended", () => setIsSpeaking(false));
    audio.addEventListener("error", () => setIsSpeaking(false));

    const playPromise = audio.play();

    if (playPromise !== undefined) {
      playPromise.catch(() => {
        setIsSpeaking(false);
      });
    }

    return () => {
      if (!audio.paused) {
        audio.pause();
      }
      audio.remove();
    };
  }, [audioUrl]);

  return (
    <div className="flex w-full flex-1 flex-col items-center justify-between">
      <div className="mb-10 flex flex-1 flex-col items-center justify-center">
        <div className="mb-6 flex h-[240px] w-[240px] items-center justify-center rounded-full border-2 border-green">
          <img alt="Kem Speaking" className="h-32 w-32" src={KemImage} />
        </div>

        {isSpeaking && (
          <div className="flex gap-2">
            <div className="h-3 w-3 animate-bounce rounded-full bg-white [animation-delay:-0.3s]" />
            <div className="h-3 w-3 animate-bounce rounded-full bg-white [animation-delay:-0.15s]" />
            <div className="h-3 w-3 animate-bounce rounded-full bg-white" />
          </div>
        )}
      </div>

      <div className="w-full max-w-md gap-3">
        <button
          className="flex h-20 w-full items-center justify-center gap-4 rounded-full border border-[#3A4047] bg-[#1E2125] px-6 transition-transform hover:scale-105 active:scale-95"
          onClick={onStartRecording}
          type="button"
        >
          <ConcentricCircles animated={isSpeaking} color="#4CAF50" />
          <Icon color="#FFFFFF" name="microphone" size={28} />
        </button>

        {/* AI Disclaimer */}
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-400">AI information can be wrong x</p>
        </div>
      </div>
    </div>
  );
};

export default KemSpeechPlaying;
